import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Space } from 'antd';
import { useDriversContext } from './context/drivers.ctx';
import { Driver } from '../../screens/drivers/driver-detail.screen';
import { DriversTable } from './DriversTable';
import { Filters } from './Filters';
import { AddDriver } from './AddDriver';
import { BulkImportDrivers } from './BulkImportDrivers';
import { TableActions } from '../TableActions';
import { IDriver } from '../../interfaces/driver.interface';
import { exportTableData } from '../../utils/export.util';
import { checkDriverStatus } from '../../utils/driver.util';

const DRIVER_COLUMNS = ['name', 'email', 'fullMobile', 'status'];

const Drivers = () => {
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const [columnsSelected, setColumnsSelected] = useState<string[]>(DRIVER_COLUMNS);
    const [openBulkImport, setOpenBulkImport] = useState(false);

    const {
        drivers,
        driver,
        setPagination,
        pagination,
        total,
        loading,
        handleSearchParam,
        fetchData,
        setOpenAddDriver,
        openAddDriver,
        handleAddDriver,
    } = useDriversContext();

    const columnLabels: Record<string, string> = useMemo(() => ({
        name: td('FULL_NAME'),
        email: td('EMAIL'),
        fullMobile: td('PHONE'),
        status: td('STATUS'),
    }), [td]);

    const handleExport = () => {
        exportTableData<IDriver>(
            drivers,
            [
                { key: 'name', label: td('FULL_NAME'), render: (v, record) => `${v} ${record.lastName}` },
                { key: 'email', label: td('EMAIL') },
                { key: 'fullMobile', label: td('PHONE'), render: (v, record) => v || `${record.countryCode} ${record.mobile}` },
                { key: 'status', label: td('STATUS'), render: (_, record) => td(checkDriverStatus(record.isBusy, record.onDutyFlag)) },
            ],
            'conductores',
        );
    };

    return (
        <div className="section">

            { driver && <Driver /> }

            {
                openAddDriver && (
                    <AddDriver
                        open={openAddDriver}
                        refreshData={fetchData}
                        handleAddDriver={handleAddDriver}
                        onCancel={() => setOpenAddDriver(false)}
                    />
                )
            }

            {
                openBulkImport && (
                    <BulkImportDrivers
                        open={openBulkImport}
                        onCancel={() => setOpenBulkImport(false)}
                        refreshData={fetchData}
                    />
                )
            }

            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Filters
                    loading={loading}
                    refreshData={fetchData}
                    handleSearchParam={handleSearchParam}
                    setOpenAddDriver={setOpenAddDriver}
                    setOpenBulkImport={setOpenBulkImport}
                    extra={(
                        <TableActions
                            columnsSelected={columnsSelected}
                            availableColumns={DRIVER_COLUMNS}
                            columnLabels={columnLabels}
                            onColumnsChange={setColumnsSelected}
                            onExport={handleExport}
                            onReload={fetchData}
                            exportDisabled={!drivers?.length}
                        />
                    )}
                />
                <DriversTable
                    drivers={drivers}
                    setPagination={setPagination}
                    pagination={pagination}
                    total={total}
                    loading={loading}
                    columnsSelected={columnsSelected}
                />
            </Space>
        </div>
    );
};

export { Drivers };
